import {
  EvmAddressSummaryInfo,
  EvmErc20TransferLogInfo,
  EvmErc721TransferLogInfo,
  EvmErc1155TransferLogInfo,
  EvmTokenSummaryInfo,
  EvmTrxInfo,
  EvmTrxListInfo
} from '~/api/repository'
import { AddressSummaryModel, TokenSummaryModel } from '~/types/model/common.model'
import { ITokenType } from '~/types/token-type.type'
import { secondsToDate } from '~/utils/dateFormat'
import { setComma, setDigits } from '~/utils/numberFormat'
import { capitalizeFirstLetter } from '~/utils/stringFormat'

export class TransactionActionModel {
  readonly _size: string
  readonly _event: string
  readonly tokenType: ITokenType
  readonly _tokenInfo: EvmTokenSummaryInfo
  readonly tokenId?: string
  readonly _fromAddressInfo: EvmAddressSummaryInfo
  readonly _toAddressInfo: EvmAddressSummaryInfo

  constructor(data: EvmErc20TransferLogInfo | EvmErc721TransferLogInfo | EvmErc1155TransferLogInfo, tokenType: ITokenType) {
    this.tokenType = tokenType
    this._event = data.EVT
    this._tokenInfo = data.TKNSINFO
    this._fromAddressInfo = data.ADDRSFROMINFO
    this._toAddressInfo = data.ADDRSTOINFO
    if ('QTY' in data) { // erc20
      this._size = data.QTY
    } else { // erc721, erc1155
      this._size = data.TKNVAL
    }
    if ('TKNID' in data) {
      this.tokenId = data.TKNID
    }
  }

  get size() {
    return setComma(this._size)
  }

  get event() {
    return capitalizeFirstLetter(this._event)
  }

  get tokenInfo() {
    return new TokenSummaryModel(this._tokenInfo, this.tokenType)
  }

  get fromAddressInfo() {
    return new AddressSummaryModel(this._fromAddressInfo)
  }

  get toAddressInfo() {
    return new AddressSummaryModel(this._toAddressInfo)
  }
}

export class TransactionModel {
  readonly blockNumber: string
  readonly transactionHash: string
  readonly age: string
  readonly _unixTimeStamp: number
  readonly _fromAddressInfo: EvmAddressSummaryInfo
  readonly _toAddressInfo: EvmAddressSummaryInfo
  readonly method: string
  readonly _value: string
  readonly _transactionFee: string
  readonly transactionId: string
  readonly sequenceNumber: string

  constructor(data: EvmTrxInfo) {
    this.blockNumber = data.BNO
    this.transactionHash = data.TRXHA
    this.age = data.AG
    this._unixTimeStamp = data.UT
    this._fromAddressInfo = data.ADDRSFROMINFO
    this._toAddressInfo = data.ADDRSTOINFO
    this.method = data.MTH
    this._value = data.VAL
    this._transactionFee = data.TRXF
    this.transactionId = data.TRXID
    this.sequenceNumber = data.SNO
  }

  get unixTimeStamp() {
    return secondsToDate(this._unixTimeStamp)
  }

  get value() {
    return setComma(setDigits(this._value, 15))
  }

  get transactionFee() {
    return setComma(setDigits(this._transactionFee, 11))
  }

  get fromAddressInfo() {
    return new AddressSummaryModel(this._fromAddressInfo)
  }

  get toAddressInfo() {
    return new AddressSummaryModel(this._toAddressInfo)
  }
}

export class TransactionListModel {
  readonly totalCount: number
  readonly viewCount: number
  private readonly _transactionList: Array<EvmTrxInfo>

  constructor(data: EvmTrxListInfo) {
    this.totalCount = Number(data.TCNT)
    this.viewCount = Number(data.VCNT)
    this._transactionList = data.TRXLIST
  }

  get formattedTotalCount() {
    return setComma(this.totalCount)
  }

  get formattedViewCount() {
    return setComma(this.viewCount)
  }

  get transactionList() {
    return this._transactionList.map((transaction) => new TransactionModel(transaction))
  }
}